import {
  type ComplianceBadgeConfig,
  getOnboardingContentBadgesForCountry,
  getPillarBadges,
  mapCountryCodeToSupportedCountry,
} from '@/components/modal/sign-in-modal/compliant-logos';
import { useIsOnboardingComplianceCopyEnabled } from '@/features/flags';
import { GET_ONBOARDING_V2_VARIANT } from '@/features/flags/legacy-flags';
import { trackScreenViewed } from '@/lib/analytics/scribe/ui';
import { cn } from '@/lib/cn';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { Link } from '@heidi/design-system/link';
import {
  TypographyH2,
  TypographyP,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import {
  getSafetyPageUrl,
  OnboardingStepShell,
  PrivacyComplianceCopy,
  tourStepsMessages,
} from '@heidi/growth';
import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useTranslations } from '@heidi/runtime/i18n';
import { Check, ExternalLink } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

interface PrivacyStepProps {
  onGetStarted: () => void;
  isSubmitting: boolean;
  countryCode?: string;
  /**
   * Guided-tour arm: condensed compliance layout (checklist + badge chips)
   * instead of the three-pillar accordion the control arm keeps.
   */
  isGuidedTour?: boolean;
}

/**
 * A compliance credential, rendered from the same `badge.text` key in both arms.
 *
 * `ticked` is the control arm's green badge. The guided tour passes
 * `ticked={false}` for a neutral sand pill, so its chips read as quiet
 * supporting credentials rather than repeating the green ticks of the checklist
 * directly above them.
 */
function BadgeLabel({
  badge,
  ticked = true,
}: {
  badge: ComplianceBadgeConfig;
  ticked?: boolean;
}) {
  const { t } = useTranslations();
  // German onboarding shows GDPR in the `lg` preview illustration. Hide the
  // content-panel copy of that credential at the same breakpoint so mobile
  // still has it and desktop does not print it twice.
  const hideWhenPreviewShowsIt = badge.alt === 'GDPR' ? 'lg:hidden' : undefined;

  if (!ticked) {
    return (
      <Badge
        size="sm"
        className={cn(
          'rounded-full bg-entity-neutral-muted px-3 py-1 text-xs leading-snug text-entity-neutral-foreground',
          hideWhenPreviewShowsIt
        )}
      >
        {t(badge.text)}
      </Badge>
    );
  }

  return (
    <Badge color="green" size="sm" className={cn('gap-1', hideWhenPreviewShowsIt)}>
      <Check className="size-3" aria-hidden />
      {t(badge.text)}
    </Badge>
  );
}

function Pillar({
  title,
  description,
  badges,
}: {
  title: string;
  description: string;
  badges?: ComplianceBadgeConfig[];
}) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-pretty text-sm font-semibold text-text-primary">{title}</p>
      <TypographyP2 className="text-pretty text-text-secondary">
        {description}
      </TypographyP2>
      {badges && badges.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {badges.map((b) => (
            <BadgeLabel key={b.alt} badge={b} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PrivacyStep({
  onGetStarted,
  isSubmitting,
  countryCode,
  isGuidedTour = false,
}: PrivacyStepProps) {
  const { t } = useTranslations();
  const country = mapCountryCodeToSupportedCountry(countryCode);
  const pillars = getPillarBadges(country);
  const showComplianceCopy = useIsOnboardingComplianceCopyEnabled();
  // Only the UK receives the regulated-device labelling. All other markets,
  // including EU/EEA and markets without a country code, receive the
  // not-a-medical-device statement.
  const showIntendedUse = showComplianceCopy && country === 'UK';

  // Fire `screen_viewed` exactly once per mount, tagged with the
  // onboarding A/B variant so funnel/conversion analysis can split by flow.
  const hasTrackedScreenView = useRef(false);
  useEffect(() => {
    if (hasTrackedScreenView.current) return;
    hasTrackedScreenView.current = true;
    trackScreenViewed({
      screen_name: ScreenName.OnboardingPrivacy,
      onboarding_variant: GET_ONBOARDING_V2_VARIANT(),
    });
  }, []);

  const safetyLink = (
    <Link
      href={getSafetyPageUrl()}
      target="_blank"
      rel="noopener noreferrer"
      variant="inline"
      size="sm"
      aria-label={t('signup.privacyComplianceLinkAriaLabel')}
      className="self-center"
    >
      {t('signup.privacyComplianceLink')}
      <ExternalLink aria-hidden />
    </Link>
  );

  // This arm carries no medical-device statement — neither the intended-use
  // block nor the EU/EEA one below. That holds only while the tour experiment
  // is off; it needs one of them before the tour ships to either market.
  if (isGuidedTour) {
    const chips = getOnboardingContentBadgesForCountry(country);
    return (
      <OnboardingStepShell
        contentClassName="gap-6"
        hideFooterDivider
        footer={
          // Design's "Button Row": the link keeps the free space on the start
          // side and the button sits at its natural width on the end side, not
          // stretched across the column. `flex-wrap` covers a long translation.
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            {safetyLink}
            <Button size="lg" onClick={onGetStarted} disabled={isSubmitting}>
              {isSubmitting
                ? t('signup.privacySettingUp')
                : t('signup.privacyGetStarted')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-6">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-full border border-semantic-text-success">
            <Check className="size-6 text-semantic-text-success" aria-hidden />
          </div>

          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <TypographyH2>{t('signup.privacyTitle')}</TypographyH2>
              <TypographyP className="text-pretty">
                <FormattedMessage {...tourStepsMessages.completionSubtitle} />
              </TypographyP>
            </div>

            <div className="flex flex-col gap-3">
              <TypographyP2Bold>
                <FormattedMessage {...tourStepsMessages.completionConfidenceTitle} />
              </TypographyP2Bold>
              <ul className="flex flex-col gap-3">
                {[
                  tourStepsMessages.completionConfidenceEncrypted,
                  tourStepsMessages.completionConfidenceTraining,
                  tourStepsMessages.completionConfidenceCertified,
                ].map((message) => (
                  <li key={message.defaultMessage} className="flex gap-2">
                    <Check
                      className="mt-0.5 size-4 shrink-0 text-semantic-text-success"
                      aria-hidden
                    />
                    <TypographyP2 className="text-pretty">
                      <FormattedMessage {...message} />
                    </TypographyP2>
                  </li>
                ))}
              </ul>
            </div>

            {chips.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {chips.map((badge) => (
                  <BadgeLabel key={badge.alt} badge={badge} ticked={false} />
                ))}
              </div>
            )}
          </div>
        </div>
      </OnboardingStepShell>
    );
  }

  return (
    <OnboardingStepShell
      // `scrollbar-none` hides the track without disabling the scroll: this step
      // stacks two scroll regions (the shell column and the reminders panel), and
      // two native bars side by side read as chrome rather than affordance.
      contentClassName="gap-8 scrollbar-none"
      footer={
        <Button
          size="lg"
          onClick={onGetStarted}
          disabled={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? t('signup.privacySettingUp') : t('signup.privacyGetStarted')}
        </Button>
      }
    >
      <div className="flex flex-col gap-2">
        <TypographyH2>{t('signup.privacyTitle')}</TypographyH2>
        <TypographyP2 className="text-text-secondary">
          {t('signup.privacySubtitle')}
        </TypographyP2>
      </div>

      <div className="flex flex-col gap-6">
        <Pillar
          title={t('signup.privacyPillarEncryptedTitle')}
          description={t('signup.privacyPillarEncryptedDesc')}
          badges={pillars.security}
        />

        <div className="border-t border-border" />

        <Pillar
          title={t('signup.privacyPillarTrainingTitle')}
          description={t('signup.privacyPillarTrainingDesc')}
          badges={pillars.regional}
        />

        {/* The certification pillar is replaced by the compliance statement
            (UK intended-use or EU not-a-medical-device). It only returns when
            the kill switch is off. */}
        {!showComplianceCopy && (
          <>
            <div className="border-t border-border" />

            <Pillar
              title={t('signup.privacyPillarCertifiedTitle')}
              description={t('signup.privacyPillarCertifiedDesc')}
              badges={pillars.certifications}
            />
          </>
        )}

        {safetyLink}

        {showComplianceCopy &&
          (showIntendedUse ? (
            <PrivacyComplianceCopy
              variant="intended-use"
              intendedUse={{
                title: t('signup.privacyIntendedUseTitle'),
                scribe: t('signup.privacyIntendedUseScribe'),
                evidence: null,
              }}
            />
          ) : (
            <PrivacyComplianceCopy variant="not-a-medical-device" />
          ))}
      </div>
    </OnboardingStepShell>
  );
}
